import React from 'react';
import styled from 'styled-components';

import {
  vendorHasDiscountDays,
  vendorHasDiscountSeasons,
  generateImageFromCDN,
  getFirstName,
} from '../helpers';

import Badges from './Badges';
import Billboard from './Billboard';
import CheckAvailability from './CheckAvailability';
import Contact from './Contact';
import EnquiryButton from './EnquiryButton';
import ExpandableCopy from './ExpandableCopy';
import Images from './Images';
import Location from './Location';
import Packages from './Packages';
import Reviews from './Reviews';
import Instagram from './Instagram';
import Questions from './Questions';
import SimilarVendors from './SimilarVendors';
import VendorDetails from './VendorDetails';
import VendorProfileSection from './VendorProfileSection';
import VideoPlayer from './VideoPlayer';

import ContactForm from './ContactForm';

import bestOf2021 from '../images/awards/best-of-weddings-2021.png';

const detailsSectionPossibilities = [
  'is_videographer',
  'destination_available',
  'digital_files_included',
  'digital_editing_included',
  'drone_available',
  'additional_shooter_available',
  'engagement_shoots_available',
  'photo_album_available',
  'printing_available',
  'performs_same_sex',
  'performs_religious',
  'performs_non_religious',
  'performs_interfaith',
  'vowel_assistance',
];

const FooterEnquiryButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  justify-content: flex-end;
`;

export default function VendorProfile({ vendor, userId = null }) {
  const vendorDetails = Object.keys(vendor.services).filter(
    key => !!detailsSectionPossibilities.includes(key)
  );
  const vendorId = vendor.id;
  const hasDiscountDays = true;
  const hasDiscountSeasons = true;

  // Make sure we have the bare minimum of content
  // before displaying the profile
  if (!vendor.business || !vendor.type) {
    return <div>This profile doesn't exist</div>;
  }

  const vendorImages = vendor.gallery
    ? vendor.gallery.map((img, index) => {
        return {
          src: generateImageFromCDN(img.url, {
            width: 1920,
            height: 1080,
            fit: 'max',
          }),
          width: 10,
          height: 10,
        };
      })
    : [];

  return (
    <div>
      <Billboard vendor={vendor} userId={userId} />
      <Images
        images={vendorImages}
        vendorId={vendorId}
        userId={userId}
        vendor={vendor}
      />

      {vendor.contact_video && (
        <VendorProfileSection title={`Meet ${getFirstName(vendor.contact)}`}>
          <VideoPlayer
            playbackUrl={vendor.contact_video.playback_url}
            thumbnailUrl={vendor.contact_video.thumbnail_url}
          />
        </VendorProfileSection>
      )}

      <VendorProfileSection title={'Details'}>
        <VendorDetails
          details={vendorDetails}
          hasDiscountDays={hasDiscountDays}
          hasDiscountSeasons={hasDiscountSeasons}
          serviceArea={vendor.service_area}
        />
      </VendorProfileSection>

      {vendor?.detail?.description && vendor?.detail?.description.length > 2 && (
        <VendorProfileSection title={'About This Business'}>
          <ExpandableCopy ignore={vendor.detail.description.length < 350}>
            {vendor.detail.description ||
              'This vendor has not provided details.'}
          </ExpandableCopy>
        </VendorProfileSection>
      )}

      {vendor?.contacts?.length > 0 &&
        vendor.contacts[0].introduction?.length > 2 && (
          <VendorProfileSection
            title={`About ${getFirstName(vendor.contacts[0].name)}`}
          >
            <ExpandableCopy
              ignore={vendor.contacts[0].introduction.length < 350}
            >
              {vendor.contacts[0].introduction}
            </ExpandableCopy>
          </VendorProfileSection>
        )}

      {!!vendor?.detail?.cancellation_policy &&
        vendor?.detail?.cancellation_policy.length > 2 && (
          <VendorProfileSection title={'Cancellation/Refund Policy'}>
            <ExpandableCopy
              ignore={vendor.detail.cancellation_policy.length < 350}
            >
              {vendor.detail.cancellation_policy}
            </ExpandableCopy>
          </VendorProfileSection>
        )}

      <VendorProfileSection title={'Check Availability'}>
        <CheckAvailability
          userId={userId}
          vendorId={vendor.id}
          type={vendor.type}
          location={vendor.area_level_1}
        />
      </VendorProfileSection>

      <Packages country={vendor.country} vendorId={vendor.id} userId={userId} />

      {vendor?.detail?.instagram ? <Instagram vendorId={vendor.id} /> : null}

      <Questions vendorId={vendor.id} userId={userId} />

      {vendor.reviews ? <Reviews reviews={vendor.reviews} /> : null}

      {vendor.badges && (
        <VendorProfileSection title="Awards + Accolades">
          <Badges badges={vendor.badges} />
        </VendorProfileSection>
      )}

      {vendor?.similarVendors && vendor.similarVendors.length && (
        <VendorProfileSection title="You may also like">
          <SimilarVendors vendors={vendor.similarVendors} />
        </VendorProfileSection>
      )}

      <VendorProfileSection title={`Get in touch with ${vendor.contact}`}>
        <FooterEnquiryButtonWrapper>
          <ContactForm
            vendorId={vendorId}
            type={vendor.type}
            userId={userId}
            layout="2col"
          />
        </FooterEnquiryButtonWrapper>
      </VendorProfileSection>
    </div>
  );
}
